import { useEffect, useRef } from 'react';

export default function AppPreviewSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="app-preview"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-midnight overflow-hidden"
    >
      {/* Background accents */}
      <div className="absolute inset-0 bg-gradient-to-b from-midnight via-royal-navy/10 to-midnight" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-champagne-gold/3 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="animate-on-scroll opacity-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-champagne-gold/30 bg-champagne-gold/10 mb-6">
              <i className="ri-smartphone-line text-champagne-gold text-sm" />
              <span className="text-champagne-gold text-sm font-medium">App Preview</span>
            </div>

            <h2 className="animate-on-scroll opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-warm-white mb-6">
              Your coaching companion,{' '}
              <span className="gold-gradient-text">anywhere you go.</span>
            </h2>

            <p className="animate-on-scroll opacity-0 text-silver-grey text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Open the app, choose your coaching style, and get instant guidance designed to move you forward.
            </p>
          </div>

          <div className="animate-on-scroll opacity-0 flex justify-center items-center">
            {/* Background removed phone image - just the device on existing background */}
            <img
              src="https://public.readdy.ai/ai/img_res/4a4ad6c1-0ae3-4a3d-a6c9-a383590cee40.png"
              alt="TNT Coach AI App Preview"
              className="relative w-80 md:w-96 drop-shadow-2xl"
            />
          </div>

          {/* Feature pills below phone */}
          <div className="animate-on-scroll opacity-0 mt-12 flex flex-wrap justify-center gap-3">
            {[
              { icon: 'ri-flashlight-line', label: 'Instant Response' },
              { icon: 'ri-shield-check-line', label: 'Private & Secure' },
              { icon: 'ri-time-line', label: '24/7 Available' },
              { icon: 'ri-user-heart-line', label: 'Personalised' },
            ].map((feature) => (
              <div
                key={feature.label}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-royal-navy/30 bg-royal-navy/20 text-silver-grey text-sm"
              >
                <i className={`${feature.icon} text-champagne-gold`} />
                <span>{feature.label}</span>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll opacity-0 mt-10 flex justify-center">
            <a
              href="https://tnt-coach-ai.base44.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full gold-gradient text-midnight font-bold text-base hover:opacity-90 transition-all duration-300 shadow-lg shadow-champagne-gold/20 whitespace-nowrap"
            >
              <i className="ri-flashlight-line" />
              Get Instant Coaching
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}